import {
  Box,
  Divider,
  Flex,
  Grid,
  GridItem,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { PortfolioCovenantResultResultGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  ExpandedPortfolioCovenantChartData,
  expandedPortfolioCovenantNonIntermediateFields,
} from '../shared/portfolioChartUtils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';

const ResultBadge = ({
  result,
}: {
  result: PortfolioCovenantResultResultGQLType;
}) => {
  return (
    <Flex
      alignItems="center"
      borderRadius="8px"
      backgroundColor={colors.green[100]}
      width="fit-content"
      padding="8px 16px"
    >
      <Icon
        icon="circle-check"
        color={result === 'PASS' ? colors.success : colors.error}
      />
      <Text ml="16px" fontWeight={500} color={colors.success}>
        {commonUtils.toTitleCase(result)}
      </Text>
    </Flex>
  );
};

const PortfolioCovenantChartDetailsGridRow = ({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: React.ReactElement;
}) => {
  return (
    <Grid templateColumns="repeat(5, 1fr)" margin="16px">
      <GridItem colSpan={2} padding="8px 0">
        <Flex alignItems="center">
          <Icon icon={icon} />
          <Text ml="16px" fontWeight={600}>
            {label}
          </Text>
        </Flex>
      </GridItem>
      <GridItem colSpan={3} pl="24px">
        {value}
      </GridItem>
    </Grid>
  );
};

export const PortfolioCovenantChartDetails = ({
  selectedDataPoint,
}: {
  selectedDataPoint: ExpandedPortfolioCovenantChartData;
}) => {
  return (
    <Box width="100%" mt="64px">
      <PortfolioCovenantChartDetailsGridRow
        icon="box-ballot"
        label="Result"
        value={<ResultBadge result={selectedDataPoint.result} />}
      />
      <Divider />
      <PortfolioCovenantChartDetailsGridRow
        icon="grip-lines"
        label="Value"
        value={
          <Text
            backgroundColor={colors.aurora[50]}
            padding="8px 16px"
            borderRadius="8px"
            fontWeight={500}
          >
            {selectedDataPoint.value}
          </Text>
        }
      />
      <Divider />
      <PortfolioCovenantChartDetailsGridRow
        icon="calendar"
        label="Reporting Date"
        value={
          <Text fontWeight={500} padding="8px 0">
            {selectedDataPoint.reportingDate}
          </Text>
        }
      />
      <Divider />
      <PortfolioCovenantChartDetailsGridRow
        icon="arrow-turn-down-right"
        label="Intermediate Values"
        value={<></>}
      />
      <Box
        margin="16px"
        borderWidth="1px"
        // maxH required for table top sticky header to work
        // since position:sticky attaches to the nearest ancestor with a scrolling mechanism
        // https://stackoverflow.com/questions/70770027/table-with-sticky-header-and-horizontal-scroll
        maxHeight="50vh"
        overflowX="auto"
        overflowY="auto"
        position="relative"
      >
        <Table style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <Thead>
            <Tr borderBottomWidth="2px">
              <Th
                w="40%"
                position="sticky"
                top="0"
                fontSize="16px"
                fontWeight={500}
                color={colors.black[95]}
              >
                Key
              </Th>
              <Th
                w="60%"
                position="sticky"
                top="0"
                fontSize="16px"
                fontWeight={500}
                color={colors.black[95]}
              >
                Value
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {_.map(selectedDataPoint, (property, key) => {
              if (
                !_.includes(expandedPortfolioCovenantNonIntermediateFields, key)
              ) {
                return (
                  <Tr color={colors.black[70]}>
                    <Td w="40%">{key}</Td>
                    <Td w="60%">{property}</Td>
                  </Tr>
                );
              }
              return null;
            })}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};
